import React, { FC } from 'react';
import classNames from 'classnames/bind';
import properties from '../../../../modules/ajio-util/properties';
import { SlaCodeLabels, SlaOption } from '../types';
import { formatCutoff, formatEddDate, formatSlaCode } from '../format';
import styles from '../index.pcss';

const cx = classNames.bind(styles);

const imageBaseUrl = properties.getProperty('imageBaseUrl');
// One asset per deliveryMethod. RUSH / PRIORITY are pre-labelled tags (they
// carry their own wordmark); STANDARD is the plain truck glyph.
const STANDARD_TRUCK = `${imageBaseUrl}/static/img/standardDelTruck.svg`;
const RUSH_TAG = `${imageBaseUrl}/static/img/rushDeliveryTagIcon.svg`;
const PRIORITY_TAG = `${imageBaseUrl}/static/img/priority_delivery_sla.svg`;
const RADIO_CHECKED = `${imageBaseUrl}/static/img/multiseller-check.svg`;
const RADIO_UNCHECKED = `${imageBaseUrl}/static/img/multiseller-uncheck.svg`;
const RADIO_CHECKED_LUXE = `${imageBaseUrl}/static/img/multiseller-check-luxe.svg`;
const RADIO_UNCHECKED_LUXE = `${imageBaseUrl}/static/img/multiseller-uncheck-luxe.svg`;

export interface SlaOptionCardProps {
  option: SlaOption;
  /** render the radio selector (multi SLA) */
  selectable?: boolean;
  /** render the priceType badge (Best / Faster) */
  showBadge?: boolean;
  selected?: boolean;
  onSelect?: (option: SlaOption) => void;
  /** radio group name to keep options mutually exclusive */
  htmlName?: string;
  /** luxe theme (black radio + badge/border) */
  luxe?: boolean;
  /** SLA-code → copy map from priorityDeliverySLPTagsConfig (PLP_BUCKET) */
  slaCodeLabels?: SlaCodeLabels;
  /** Cart only — suffix the amount with "/ pc" (prices there are per piece). */
  perPiece?: boolean;
  /** Pincode the options were requested with — named in the unserviceable copy. */
  activePincode?: string;
}

/** One SLA option — radio (optional) + delivery info + badge + per-seller price. */
const SlaOptionCard: FC<SlaOptionCardProps> = ({
  option,
  selectable = false,
  showBadge = false,
  selected = false,
  onSelect,
  htmlName = 'sla-option',
  luxe = false,
  slaCodeLabels,
  perPiece = false,
  activePincode = ''
}) => {
  const radioChecked = luxe ? RADIO_CHECKED_LUXE : RADIO_CHECKED;
  const radioUnchecked = luxe ? RADIO_UNCHECKED_LUXE : RADIO_UNCHECKED;
  const { serviceable, delivery, price, tagLabel, reasonForNotServiceability } = option;
  const { deliveryMethod } = delivery;
  const eddText = formatEddDate(delivery.eddUpper);
  const cutoffText = formatCutoff(delivery.cutOffTime);

  // The icon is driven purely by deliveryMethod, not by priceType.
  const isRush = deliveryMethod === 'RUSH';
  const isPriority = deliveryMethod === 'PRIORITY';
  const isExpress = isRush || isPriority;
  const methodIcon = isRush ? RUSH_TAG : isPriority ? PRIORITY_TAG : STANDARD_TRUCK;
  const methodAlt = isRush ? 'Rush delivery' : isPriority ? 'Priority delivery' : '';
  // Express rows use the configured SLA copy ("4 hours" / "2 Days, Fri") under a
  // "Delivery in" label; standard keeps "Delivery by <date>". Priority also names
  // the delivery day — "90 minutes, Mon".
  const slaText = isExpress
    ? formatSlaCode(delivery.deliverySLA, slaCodeLabels, delivery.eddUpper, isPriority)
    : '';
  const deliveryText = slaText || eddText;
  const deliveryLabel = slaText ? 'Delivery in' : 'Delivery by';

  const { current, was, discount } = price;
  const hasDiscount = Boolean(discount && was && was !== current);
  const hasPrice = Boolean(current || was);

  const disabled = !serviceable;
  const handleSelect = () => {
    if (disabled || !selectable) return;
    onSelect?.(option);
  };

  // Not deliverable — the reason stands on its own: no radio (there is nothing
  // to pick) and no card, so it reads as a message rather than an option.
  if (disabled) {
    return (
      <div className={cx('sla-unserviceable')} role="alert">
        {reasonForNotServiceability === 'NS' && activePincode
          ? `Currently we do not deliver to ${activePincode}`
          : 'This product is not deliverable to your location or out of stock'}
      </div>
    );
  }

  return (
    <div
      className={cx('sla-card', { selected })}
      role={selectable ? 'radio' : undefined}
      aria-checked={selectable ? selected : undefined}
    >
      <div className={cx('sla-card-main')}>
        <div className={cx('sla-card-left')}>
          {(selectable || selected) && (
            <label className={cx('sla-radio')}>
              <input
                type="radio"
                name={htmlName}
                checked={selected}
                disabled={!selectable}
                onChange={handleSelect}
                aria-label={tagLabel}
              />
              <img
                src={selected ? radioChecked : radioUnchecked}
                alt=""
                aria-hidden="true"
                className={cx('sla-radio-img')}
              />
            </label>
          )}

          <div className={cx('sla-delivery')} role="presentation" onClick={handleSelect}>
            <span className={cx('sla-deliver-label')}>{deliveryLabel}</span>
            <div className={cx('sla-delivery-row')}>
              <strong className={cx('sla-date')}>{deliveryText}</strong>
              {deliveryMethod && (
                <img
                  src={methodIcon}
                  alt={methodAlt}
                  aria-hidden={isExpress ? undefined : 'true'}
                  className={cx('sla-method-img', { 'sla-method-img--express': isExpress })}
                />
              )}
            </div>
            {cutoffText && <span className={cx('sla-cutoff')}>{cutoffText}</span>}
          </div>
        </div>

        {/* badge (sla label) + price grouped together */}
        <div className={cx('sla-right')}>
          {showBadge && tagLabel && (
            <div className={cx('sla-badge')}>
              <p className={cx('sla-badge-text')}>{tagLabel}</p>
            </div>
          )}

          {hasPrice && (
            <div className={cx('sla-price')}>
              {hasDiscount ? (
                <>
                  {/* "/ pc" qualifies whichever amount is last — the discount when present,
                      else the current price. */}
                  <div className={cx('sla-price-row')}>
                    <strong className={cx('sla-price-current')}>{current}</strong>
                    {discount && <span className={cx('sla-price-discount')}>{discount}</span>}
                    {perPiece && <span className={cx('sla-price-per-piece')}>/ pc</span>}
                  </div>
                  {was && (
                    <span className={cx('sla-price-mrp')}>
                      {'MRP '}
                      <span className={cx('sla-price-mrp-strike')}>{was}</span>
                    </span>
                  )}
                </>
              ) : (
                <span className={cx('sla-price-mrp')}>
                  {`MRP ${current || was}`}
                  {perPiece && <span className={cx('sla-price-per-piece')}>{' / pc'}</span>}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SlaOptionCard;
